/**
 * The accounts that turned push on in this browser (docs/MultiAccountSessions.md §8).
 *
 * Kept in IndexedDB because the service worker reads it too, with no page open: it labels a
 * notification with its account when more than one receives push here, and when the push
 * service rotates the browser's endpoint it re-enrols each of these accounts. `public/sw.js`
 * opens the same database with the same version and store, so change both together.
 *
 * Best effort throughout. Private windows and hardened browsers can refuse IndexedDB, and
 * losing this record costs a label and a self-repair, never push itself: the backend's holds
 * are what deliver.
 */

const DB_NAME = 'witina-push';
const DB_VERSION = 1;
const STORE = 'accounts';

export interface PushAccount {
  /** The account's user id. */
  id: string;
  /** What a notification shows to say whose it is: the account's email address. */
  label: string;
  /** The endpoint the account enrolled with, so a changed one reads as a rotation. */
  endpoint: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB open failed'));
    };
  });
}

async function run<T>(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest, fallback: T): Promise<T> {
  try {
    if (typeof indexedDB === 'undefined') return fallback;
    const db = await open();
    try {
      return await new Promise<T>((resolve, reject) => {
        const request = op(db.transaction(STORE, mode).objectStore(STORE));
        request.onsuccess = () => {
          resolve((request.result as T | undefined) ?? fallback);
        };
        request.onerror = () => {
          reject(request.error ?? new Error('IndexedDB request failed'));
        };
      });
    } finally {
      db.close();
    }
  } catch {
    return fallback;
  }
}

export function pushAccount(id: string): Promise<PushAccount | undefined> {
  return run<PushAccount | undefined>('readonly', (store) => store.get(id), undefined);
}

export async function rememberPushAccount(account: PushAccount): Promise<void> {
  await run('readwrite', (store) => store.put(account), undefined);
}

export async function forgetPushAccount(id: string): Promise<void> {
  await run('readwrite', (store) => store.delete(id), undefined);
}

export async function forgetAllPushAccounts(): Promise<void> {
  await run('readwrite', (store) => store.clear(), undefined);
}
